/**
 * The articles listing's facet vocabulary, beyond the publish-date presets in
 * `published-within`.
 *
 * Every one of these resolves against a column the article query can already
 * reach, which is what keeps this listing off Elasticsearch: there is nothing
 * to index and nothing to aggregate.
 */

/** URL parameter and filter-state key for the cross-category narrow. */
export const ALSO_IN_FILTER_KEY = 'alsoInCategory';

/** URL parameter and filter-state key for the video narrow. */
export const HAS_VIDEOS_FILTER_KEY = 'hasVideos';

/**
 * URL parameter for the listing order.
 *
 * Deliberately not `sort`: that one is configured site-wide as a *path* segment
 * (`/sort_popular`), which on this route would be swallowed by the category
 * splat and read as a sub-category that does not exist.
 */
export const ORDER_PARAM = 'order';

/**
 * Every URL parameter that narrows the listing.
 *
 * The loader reads this to tell an empty *filtered* listing (render it, with an
 * empty state) from a category that does not exist (a real 404). Order is left
 * out on purpose: it re-ranks the set, it never empties it.
 */
export const ARTICLE_FACET_PARAMS = [
    'publishedWithin',
    ALSO_IN_FILTER_KEY,
    HAS_VIDEOS_FILTER_KEY,
];

/**
 * Theme selections travel as one parameter per dimension, so they cannot be
 * listed above — the vocabulary decides the names. Prefix-matched instead.
 *
 * A facet missing from this decision turns an empty filtered listing into a
 * 404, telling the reader the page moved and throwing away the filters they
 * just set.
 */
export const THEME_FACET_PARAM_PREFIX = 'theme-';

/** Whether any facet on this listing is narrowing the current request. */
export const hasActiveArticleFacets = (
    searchParams: URLSearchParams,
): boolean =>
    ARTICLE_FACET_PARAMS.some((param) => searchParams.has(param)) ||
    [...searchParams.keys()].some((key) =>
        key.startsWith(THEME_FACET_PARAM_PREFIX),
    );

export type ArticleOrder = 'newest' | 'popular';

export const ARTICLE_ORDERS: { value: ArticleOrder; label: string }[] = [
    { value: 'newest', label: 'Newest' },
    { value: 'popular', label: 'Most popular' },
];

/** Newest is the listing's standing order, so it is what an absent param means. */
export const DEFAULT_ARTICLE_ORDER: ArticleOrder = 'newest';

export const validateArticleOrder = (
    value: string | undefined | null,
): ArticleOrder => {
    const match = ARTICLE_ORDERS.find((order) => order.value === value);
    return match?.value ?? DEFAULT_ARTICLE_ORDER;
};

/**
 * The connection sort for an order. Both fields exist on `mv_Article` and are
 * declared sortable on the connection; ID breaks ties so paging stays stable
 * across requests when two articles share a date or a popularity score.
 */
export const getArticleSort = (
    order: ArticleOrder,
): Record<string, 'ASC' | 'DESC'> =>
    order === 'popular'
        ? { Popularity: 'DESC', ID: 'DESC' }
        : { PublishDate: 'DESC', ID: 'DESC' };
